<template>
  <div class="dropdown">
    <button
      v-clicked-outside="{onClick: clickedOutside, oNode: `v-overideClick-${ref}`}"
      @click="toggleShowSelectionOptions"
      v-bind:class="{'sb-button--active': showSelectionOptions}"
      class="d-flex align-items-center btn btn-primary dropdown-toggle sb-button"
      :style="styleOverrides"
      type="button"
    >
      <span v-if="showActiveSelection">{{ activeSelection | objectToString(displayValue) | i18n($t) }}</span>
      <span v-if="multiselectable" class="d-flex">
        <div v-if="hasActiveSelections" v-for="selection in activeSelections" :key="selectionKey(selection)" class="d-flex active-selection">
          <div>{{ selection | objectToString(displayValue) | i18n($t) }}</div>
          <button @click.stop="changeActiveSelection(selection)" type="button" class="close">
            <span>&times;</span>
          </button>
        </div>
        <span v-if="!hasActiveSelections">{{ defaultDropdownText }}</span>
      </span>
      <input
        v-focus="!multiselectable"
        v-if="enableSearch"
        v-model="searchTerm"
        @click.stop="toggleShowSelectionOptions(true)"
        v-on:keyup.prevent="changeActiveSearch()"
        id="v-searchBox"
        type="text"
        class="form-control form--overides"
        v-bind:placeholder="placeholderText"
      >
    </button>
    <span v-if="showSelectionOptions" class="carrot"></span>
    <div
      v-bind:class="{'dropdown-menu--show': showSelectionOptions}"
      v-bind:id="`v-overideClick-${ref}`"
      class="dropdown-menu dropdown-menu-right dropdown-menu--override"
    >
      <a
        v-if="hasSelectionOptions"
        v-for="selection in selectionOptions"
        :key="selectionKey(selection)"
        @click.prevent="changeActiveSelection(selection)"
        class="dropdown-item dropdown-item---overide"
      >{{ selection | objectToString(displayValue) | i18n($t) }}</a>
      <p v-if="!hasSelectionOptions" class="text-muted empty-options">{{ emptyOptionsText }}</p>
    </div>
  </div>
</template>

<script>
import debounce from '@/utils/debounce'

/*
  Dropdown UserGuide:
    Description: This dropdown supports single select, multiselect, and a search feature.
      The default behavior of this dropdown functions like a normal single select dropdown.
      Setting the :multiselectable or :searchable prop to true will make the dropdown a
      multiselect dropdown or enable the search feature.

    Props:
      disableDropdown
       - type: Boolean
       - default: false
       - description: this will enable/disable the dropdown of list options from appearing
            when disabled, the dropdown will not show on click
      selectionOptions
       - type: String[]
       - default: true
       - description: this the list of options that the user can select from
      searchable
       - type: Boolean
       - default: false
       - description: this is the flag that will enable/disable the search feature
      multiselectable
       - type: Boolean
       - default: false
       - description: this is the flag that will turn the single select into a multiselect
            when true, you can select more than one item from the selectionOptions
      defaultDropdownText
       - type: String
       - description: This is the text that will be displayed in the dropdown button if
            the selection prop is empty or undefined
      emptyOptionsText
       - type: String
       - default: 'No results found.'
       - description: This is the text that will be displayed in the selections dropdown
            if there are no options to choose from
      selection
       - type: String | Object
       - how-to-use: <dropdown :selection.sync="<var-to-hold-selection-value>"></dropdown>
       - description: this is used when the dropdown is operating as a single select dropdown.
            this prop is the value of the dropdown. You can use the sync pattern to keep
            this value up to date with the model in the parent
            ** If you pass in an object for selections, the object must have either a 'displayValue'
            key or a key that matches what you passed in for displayValue - on that object,
            this is the text that will render on the page for that object **
      selections
       - type: String[] | Object[]
       - how-to-use: <dropdown :selections.sync="<var-to-hold-selections-value>"></dropdown>
       - description: this is used when the dropdown is operating as a multiselect dropdown.
            this prop is the values of the dropdown. You can use the sync pattern to keep
            this value up to date with the model in the parent. It is important to define
            the defaultDropdownText when the dropdown is multiselectable because it is possible
            to have no selections
            ** If you pass in an object for selections, the object must have either a 'displayValue'
            key or a key that matches what you passed in for displayValue - on that object,
            this is the text that will render on the page for that object **
      displayValue
       - type: String
       - default: displayValue
       - description: this is used when selection or selections is an object. This is the name
            of a key on that object to be used as the value that will be rendered in the dropdown
      keyValue
        - type: String
        - default: undefined
        - description: this is used by Vue to identify uniqueness between list items. It is a good
            idea to keep the displayValue of the list items unique, but if this is not possible,
            you can use this prop to identify the entry

      Events:
        search
         - how-to-use: <dropdown :searchable="true" @search="<handle-search-method>"></dropdown>
         - description: when searchable is set to true, typing into the search box will emit an
              event named search. In your eventHandler the first parameter will be a string of
              the search term. The responsibility for handling the search is on the parent
              component
*/

export default {
  name: 'Dropdown',
  props: {
    disableDropdown: {
      type: Boolean,
      default: false
    },
    selectionOptions: {
      type: Array,
      required: true
    },
    searchable: {
      type: Boolean,
      default: false
    },
    multiselectable: {
      type: Boolean,
      default: false
    },
    defaultDropdownText: {
      type: String
    },
    emptyOptionsText: {
      type: String,
      default: 'No results found.'
    },
    selection: {},
    selections: {
      type: Array
    },
    displayValue: {
      type: String,
      default: 'displayValue'
    },
    keyValue: {
      type: String
    },
    searchPlaceholder: {
      type: String,
      default: 'search'
    },
    styleOverrides: {
      type: Object,
      default: () => {}
    }
  },
  data () {
    return {
      showSelectionOptions: false,
      searchTerm: '',
      changeActiveSearch: debounce.apply(function () {
        this.$emit('search', this.searchTerm)
      }, 500, this),
      ref: null
    }
  },
  created () {
    this.ref = this._uid
  },
  computed: {
    activeSelection () {
      // handle empty object case
      if (typeof this.selection === 'object' && Object.keys(this.selection).length === 0) return this.defaultDropdownText
      return this.selection || this.defaultDropdownText
    },
    activeSelections () {
      return this.selections
    },
    enableSearch () {
      if (!this.searchable) return false
      if (this.multiselectable) return true
      if (this.showSelectionOptions) return true
      return false
    },
    hasActiveSelections () {
      return this.selections.length > 0
    },
    hasSelectionOptions () {
      return this.selectionOptions.length > 0
    },
    hasSearchTerm () {
      return this.searchTerm.length > 0
    },
    placeholderText () {
      return this.searchPlaceholder
    },
    showActiveSelection () {
      return !this.multiselectable
    }
  },
  methods: {
    changeActiveSelection (newSelection) {
      if (!this.multiselectable) {
        this.$emit('update:selection', newSelection)
        this.$emit('updateSelection', newSelection) // If using as a dynamic component
        this.toggleShowSelectionOptions()
      } else {
        const newSelections = this.selections.splice(0)
        const i = typeof newSelection === 'object' ? newSelections.map(ns => ns[this.displayValue]).indexOf(newSelection[this.displayValue]) : newSelections.indexOf(newSelection)
        i < 0 ? newSelections.push(newSelection) : newSelections.splice(i, 1)
        this.$emit('update:selections', newSelections)
        this.$emit('updateSelections', newSelections) // If using as a dynamic component
      }
      this.resetSearch()
    },
    clickedOutside () {
      if (!this.showSelectionOptions) return
      this.toggleShowSelectionOptions()
    },
    resetSearch () {
      if (this.searchable && this.searchTerm.length > 0) {
        this.searchTerm = ''
        this.changeActiveSearch()
      }
    },
    selectionKey (s) {
      return typeof s === 'object' ? s[this.keyValue || this.displayValue] : s
    },
    toggleShowSelectionOptions (force) {
      if (this.disableDropdown) return
      typeof force === 'boolean' && force ? this.showSelectionOptions = true : this.showSelectionOptions = !this.showSelectionOptions
    }
  },
  directives: {
    'clicked-outside': {
      bind: function (el, binding, vNode) {
        // Binding must be a function for this to work.
        const bubble = binding.modifiers.bubble
        const handler = (e) => {
          const nodeOverrideId = binding.value.oNode || ''
          const nodeOverride = document.getElementById(nodeOverrideId)
          if (bubble || (!el.contains(e.target) && el !== e.target)) {
            if (nodeOverride && (!nodeOverride.contains(e.target) && nodeOverride !== e.target)) {
              binding.value.onClick(e)
            }
          }
        }
        el.__vueClickOutside = handler
        document.addEventListener('click', handler)
      },
      unbind: function (el, binding) {
        document.removeEventListener('click', el.__vueClickOutside)
        el.__vueClickOutside = null
      }
    },
    'focus': {
      inserted: function (el, binding) {
        if (binding.value) el.focus()
      }
    }
  },
  filters: {
    objectToString: function (o, value) {
      return typeof o === 'object' ? o[value] : o
    },
    i18n: function (value, t) {
      return t(value)
    }
  }
}
</script>

<style scoped lang="scss">
  // TODOJB: These styles will be moved when this component goes into the design system.
  // Don't look at it straight on. ;)
  @import '../../../assets/scss/shipbob-custom/components/utils/dropdown.scss';
</style>
